/**
 * Basic assertion with (a possible lazy) message.
 * Pay attention, it will throw an error if the value is falsy, even for 0, false, and empty strings.
 * If these cases are not desired, use `assertDefined` or `assertDefinedNotNull` instead.
 * @param value
 * @param message
 */
export function assert(value, message = `value ${String(value)} is falsy`) {
    if (!value) {
        const finalMessage = typeof message === 'function' ? message() : message;
        throw new Error(finalMessage);
    }
}
/**
 * Asserts that the given value is unreachable.
 * Whenever possible, use ts-pattern instead.
 * @param value
 * @example switch-case
 * ```ts
 * type MyEnum = 'a' | 'b' | 'c';
 * const myEnum: MyEnum = 'a';
 *
 * switch (myEnum) {
 *  case 'a':
 *  case 'b':
 *  case 'c':
 *    break;
 *  default:
 *    assertUnreachable(myEnum);
 * }
 * ```
 */
export function assertUnreachable(value) {
    throw new Error(`unreachable: ${String(value)}`);
}
/**
 * Ensures that the given value is not undefined.
 * Useful to validate that value gotten from an array or record object is defined.
 * Pay attention, it will throw an error only if the value is undefined.
 * null will not throw an error. If you need it to throw an error for null, use `assertDefinedNotNull` instead.
 * @param value
 * @example get map item previously set
 * ```ts
 * const map = new Map<string, number>();
 * map.set('foo', 20);
 * map.set('bar', 22);
 * const foo = map.get('foo');
 * const bar = map.get('bar');
 * assertDefined(foo);
 * assertDefined(bar);
 * console.log(`Answer to the Ultimate Question of Life, the Universe, and Everything is ${foo + bar}`);
 * ```
 */
export function assertDefined(value) {
    if (value === undefined) {
        throw new Error(`unexpected undefined value`);
    }
}
/**
 * Ensures that the given value is not nullish.
 * @param value
 * @example React Ref
 * ```ts
 * function Component() {
 *  const ref = useRef<HTMLSpanElement>(null);
 *  function onClick() {
 *    const span = ref.current;
 *    assertNotNullish(span);
 *    span.textContent = 'Hello world!';
 *  }
 *
 *  return (
 *    <div>
 *      <button onClick={onClick}>Click me</button>
 *      <span ref={ref}></span>
 *    </div>
 *  )
 * }
 *
 * ```
 */
export function assertNotNullish(value) {
    // catch null and undefined
    if (value == null) {
        throw new Error(`unexpected ${String(value)} value`);
    }
}
/**
 * Asserts that the given value is in the given array.
 * If the call does not throw, the value is narrowed to the type of the array elements.
 * @param value - The value to check.
 * @param values - The constraint values.
 * @example
 * ```ts
 * type Unit = "ppm" | "hz" | "pt" | "s";
 * type Mode = "fid" | "ft";
 * const fidUnits = ["pt", "s"] as const;
 * const ftUnits = ["pt", "ppm", "hz"] as const;
 *
 * function setUnit(unit: Unit, mode: Mode) {
 *   match(mode)
 *     .with('fid', (mode) => {
 *       assertIn(unit, fidUnits);
 *       // unit is narrowed to "pt" | "s"
 *       return dispatch({
 *         type: 'SET_AXIS_UNIT_1D_HORIZONTAL',
 *         payload: { nucleus, mode, unit },
 *       });
 *     })
 *     .with('ft', (mode) => {
 *       assertIn(unit, ftUnits);
 *       // unit is narrowed to "pt" | "ppm" | "hz"
 *       return dispatch({
 *         type: 'SET_AXIS_UNIT_1D_HORIZONTAL',
 *         payload: { nucleus, mode, unit },
 *       });
 *     })
 *     .exhaustive();
 * }
 * ```
 */
export function assertIn(value, values) {
    if (values.includes(value))
        return;
    throw new Error(`Value ${String(value)} is not in [${values.join(',')}]`);
}
/**
 * @param value
 * @deprecated Use `assertNotNullish` instead.
 */
export function assertDefinedNotNull(value) {
    assertNotNullish(value);
}
//# sourceMappingURL=assert.js.map